import { useEffect } from "react";

/**
 * Custom hook to fix Bootstrap collapse issues when using with Tailwind CSS
 * This removes empty inline styles that interfere with CSS rules
 */
const useBootstrapCollapsesFix = () => {
  useEffect(() => {
    // Function to fix collapse elements
    const fixCollapses = () => {
      // Find all collapse elements with empty inline styles
      const collapses = document.querySelectorAll('.collapse[style=""], .collapse[style=" "]');

      collapses.forEach((collapse) => {
        // Remove the empty style attribute
        collapse.removeAttribute("style");

        // If it has the 'show' class, ensure it's visible
        if (collapse.classList.contains("show")) {
          // Force display block
          collapse.style.display = "block";
          collapse.style.visibility = "visible";
          collapse.style.opacity = "1";
          collapse.style.height = "auto";
        }
      });
    };

    // Function to fix file inputs and dropdowns
    const fixUIElements = () => {
      // Hide all native file input UI elements
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        // Ensure file inputs are properly hidden
        if (!input.classList.contains("dropzone-input") && !input.hasAttribute("data-fixed")) {
          input.style.position = "absolute";
          input.style.width = "1px";
          input.style.height = "1px";
          input.style.padding = "0";
          input.style.margin = "-1px";
          input.style.overflow = "hidden";
          input.style.clip = "rect(0, 0, 0, 0)";
          input.style.whiteSpace = "nowrap";
          input.style.border = "0";
          input.setAttribute("data-fixed", "true");
        }
      });

      // Remove dropdown carets
      const dropdownToggles = document.querySelectorAll(".dropdown-toggle::after");
      dropdownToggles.forEach((toggle) => {
        if (toggle.parentElement) {
          toggle.parentElement.classList.add("no-caret");
        }
      });

      // Fix Create button container to prevent wrapping
      const clientButtonContainers = document.querySelectorAll(".client-buttons-container");
      clientButtonContainers.forEach((container) => {
        container.style.display = "flex";
        container.style.flexWrap = "nowrap";
        container.style.gap = "0.5rem";

        // Ensure buttons don't wrap
        const buttons = container.querySelectorAll(".btn, .dropdown");
        buttons.forEach((btn) => {
          btn.style.whiteSpace = "nowrap";
          btn.style.flexShrink = "0";
        });
      });

      // Fix all buttons with left icons to prevent text wrapping
      const buttonsWithIcons = document.querySelectorAll(
        ".btn.left-icon, .custom-button.left-icon",
      );
      buttonsWithIcons.forEach((button) => {
        button.style.display = "inline-flex";
        button.style.flexDirection = "row";
        button.style.flexWrap = "nowrap";
        button.style.alignItems = "center";
        button.style.whiteSpace = "nowrap";
        button.style.gap = "0.375rem";

        // Fix icon
        const icon = button.querySelector(".icon, svg");
        if (icon) {
          icon.style.display = "inline-block";
          icon.style.flexShrink = "0";
          icon.style.marginRight = "0.375rem";
        }

        // Fix text span
        const span = button.querySelector("span");
        if (span) {
          span.style.display = "inline-block";
          span.style.whiteSpace = "nowrap";
        }
      });

      // Specific fix for document card buttons
      const documentCardButtons = document.querySelectorAll(
        ".document-card .btn, .buttons-group .btn",
      );
      documentCardButtons.forEach((button) => {
        button.style.display = "inline-flex";
        button.style.flexWrap = "nowrap";
        button.style.alignItems = "center";
        button.style.whiteSpace = "nowrap";
        button.style.width = "auto";
        button.style.minWidth = "fit-content";
      });

      // Fix document actions dropdown hamburger menu buttons
      const documentActionButtons = document.querySelectorAll(
        ".document-actions-dropdown .dropdown-btn, .document-actions-dropdown .btn.only-icon",
      );
      documentActionButtons.forEach((button) => {
        button.style.width = "40px";
        button.style.height = "40px";
        button.style.padding = "0";
        button.style.display = "flex";
        button.style.alignItems = "center";
        button.style.justifyContent = "center";
        button.style.boxSizing = "border-box";

        // Fix the icon inside
        const icon = button.querySelector(".icon, svg");
        if (icon) {
          icon.style.margin = "0";
          icon.style.padding = "0";
          icon.style.display = "block";
          icon.style.width = "18px";
          icon.style.height = "18px";
        }
      });
    };

    // Run immediately
    fixCollapses();
    fixUIElements();

    // Set up MutationObserver to watch for dynamic changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "style") {
          const { target } = mutation;
          if (target.classList.contains("collapse") && target.getAttribute("style") === "") {
            fixCollapses();
          }
        }

        // Also check for new file inputs or dropdowns
        if (mutation.type === "childList") {
          fixUIElements();
        }
      });
    });

    // Observe all collapse elements
    document.querySelectorAll(".collapse").forEach((collapse) => {
      observer.observe(collapse, {
        attributes: true,
        attributeFilter: ["style", "class"],
      });
    });

    // Also listen for DOM changes (new collapses added)
    const bodyObserver = new MutationObserver(() => {
      fixCollapses();
      fixUIElements();
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });

    // Cleanup
    return () => {
      observer.disconnect();
      bodyObserver.disconnect();
    };
  }, []);
};

export default useBootstrapCollapsesFix;
